import { useEffect, useRef, useId } from 'react';

interface SidePanelProps {
  /** Visible heading shown in the panel header — wired up via aria-labelledby. */
  title: string;
  /** Close handler; invoked on the named close button + Escape. */
  onClose: () => void;
  children: React.ReactNode;
  /** Override the close-button label when "Close panel" reads awkwardly in context. */
  closeLabel?: string;
  /** Additional className for the outer wrapper (e.g. fixed/static positioning). */
  className?: string;
}

/**
 * SidePanel — non-modal right-side overlay.
 *
 *   - role="dialog" + aria-modal="false" (it's a complementary
 *     overlay, not a modal — keyboard users can still reach the
 *     channel content beneath it).
 *   - aria-labelledby points at the panel heading.
 *   - Named close button — defaults to "Close panel", overridable.
 *   - Escape closes the panel (matches Modal's expectation).
 *   - On open, focus moves to the first focusable inside the panel.
 *   - On close, focus restores to whatever owned focus before opening
 *     (typically the trigger button in the channel header).
 */
export function SidePanel({
  title,
  onClose,
  children,
  closeLabel = 'Close panel',
  className = '',
}: SidePanelProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;

    const id = window.setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (first) first.focus();
      else root.focus();
    }, 0);

    return () => {
      window.clearTimeout(id);
      try {
        previouslyFocused.current?.focus?.();
      } catch {
        /* trigger gone */
      }
    };
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      ref={contentRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={
        'flex h-full w-80 flex-col border-l border-warm-border-light bg-warm-white outline-none dark:border-warm-dark-border dark:bg-warm-dark-surface ' +
        className
      }
    >
      <div className="flex items-center justify-between border-b border-warm-border-light px-4 py-3 dark:border-warm-dark-border">
        <h3 id={titleId} className="font-semibold text-warm-gray-800 dark:text-white">
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="flex h-6 w-6 items-center justify-center rounded text-warm-gray-400 transition hover:bg-warm-gray-100 hover:text-warm-gray-600 dark:hover:bg-white/10 dark:hover:text-warm-gray-200"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      {children}
    </div>
  );
}
